import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useMutation } from '@apollo/client'

import { useToast } from 'Components/Toast/useToast'

import type {
  mutationAddTagsForRentals,
  mutationAddTagsForRentalsVariables,
} from 'Constants/graphql/mutations/__generated__/mutationAddTagsForRentals'
import type {
  mutationRemoveTagsForRentals,
  mutationRemoveTagsForRentalsVariables,
} from 'Constants/graphql/mutations/__generated__/mutationRemoveTagsForRentals'
import { MUTATION_ADD_TAGS_FOR_RENTALS } from 'Constants/graphql/mutations/MutationAddTagsForRentals'
import { MUTATION_REMOVE_TAGS_FOR_RENTALS } from 'Constants/graphql/mutations/MutationRemoveTagsForRentals'

import type { TAG_COLOR } from 'Portal/__generated__/globalTypes'

import type { Tag, TagColor } from './tagging.types'
import { TagToastContent } from './TagToastContent'
import { useMutationErrorHandler } from './useMutationErrorHandler'

type TagMutationAction = 'add' | 'remove'

interface UseRentalTagMutationActionsOptions {
  getRentalIdsWithTag: (rentalIds: string[], tag: Tag) => string[]
  onAddCompleted: (affectedRentalIds: string[]) => Promise<void> | void
  onMutationError?: (action: TagMutationAction) => void
  onMutationStart?: (action: TagMutationAction) => void
  onNoAffected?: (action: TagMutationAction) => void
  onRemoveCompleted: (affectedRentalIds: string[]) => Promise<void> | void
  orgId: string
  orgTagsRef: { current: Tag[] }
}

export const useRentalTagMutationActions = (
  options: UseRentalTagMutationActionsOptions,
) => {
  const {
    getRentalIdsWithTag,
    onAddCompleted,
    onMutationError,
    onMutationStart,
    onNoAffected,
    onRemoveCompleted,
    orgId,
    orgTagsRef,
  } = options
  const { hideToast, showToast } = useToast()
  const intl = useIntl()
  const handleMutationError = useMutationErrorHandler()

  const [executeAddTags, { loading: isAddingTag }] = useMutation<
    mutationAddTagsForRentals,
    mutationAddTagsForRentalsVariables
  >(MUTATION_ADD_TAGS_FOR_RENTALS)

  const [executeRemoveTags, { loading: isRemovingTag }] = useMutation<
    mutationRemoveTagsForRentals,
    mutationRemoveTagsForRentalsVariables
  >(MUTATION_REMOVE_TAGS_FOR_RENTALS)

  const completeAdd = useCallback(
    (affectedRentalIds: string[]) =>
      Promise.resolve(onAddCompleted(affectedRentalIds)).catch(() => {}),
    [onAddCompleted],
  )

  const completeRemove = useCallback(
    (affectedRentalIds: string[]) =>
      Promise.resolve(onRemoveCompleted(affectedRentalIds)).catch(() => {}),
    [onRemoveCompleted],
  )

  const addTag = useCallback(
    (rentalIds: string[], name: string, color: TagColor) => {
      if (!orgId) {
        return
      }

      onMutationStart?.('add')
      executeAddTags({
        onCompleted: data => {
          const affectedItems = data.addTagsForRentals.items ?? []
          const affectedRentalIds = Array.from(
            new Set(affectedItems.map(item => String(item.taggable_id))),
          )

          if (affectedRentalIds.length === 0) {
            onNoAffected?.('add')

            return
          }

          const completionPromise = completeAdd(affectedRentalIds)

          showToast({
            content: (
              <TagToastContent
                message={intl.formatMessage(
                  { id: 'Tags_added' },
                  { count: affectedRentalIds.length, name },
                )}
                onUndo={async () => {
                  await completionPromise

                  const tagId = orgTagsRef.current.find(
                    t => t.name === name,
                  )?.id

                  if (!tagId) {
                    return
                  }

                  hideToast()
                  onMutationStart?.('remove')
                  executeRemoveTags({
                    onCompleted: () => {
                      completeRemove(affectedRentalIds)
                    },
                    onError: (error, errorOptions) => {
                      onMutationError?.('remove')
                      handleMutationError(
                        'removeTagsForRentals',
                        'Tags_remove_error',
                      )(error, errorOptions)
                    },
                    variables: {
                      input: {
                        orgID: orgId,
                        rentalIDs: affectedRentalIds,
                        tagIDs: [tagId],
                      },
                    },
                  })
                }}
              />
            ),
            variant: 'success',
          })
        },
        onError: (error, errorOptions) => {
          onMutationError?.('add')
          handleMutationError('addTagsForRentals', 'Tags_add_error')(
            error,
            errorOptions,
          )
        },
        variables: {
          input: {
            orgID: orgId,
            rentalIDs: rentalIds,
            tags: [{ color: color as TAG_COLOR, name }],
          },
        },
      })
    },
    [
      orgId,
      onMutationStart,
      executeAddTags,
      completeAdd,
      showToast,
      intl,
      orgTagsRef,
      hideToast,
      executeRemoveTags,
      completeRemove,
      onMutationError,
      handleMutationError,
      onNoAffected,
    ],
  )

  const removeTag = useCallback(
    (rentalIds: string[], tag: Tag) => {
      if (!orgId) {
        return
      }

      const affectedRentalIds = getRentalIdsWithTag(rentalIds, tag)

      if (affectedRentalIds.length === 0) {
        onNoAffected?.('remove')

        return
      }

      onMutationStart?.('remove')
      executeRemoveTags({
        onCompleted: () => {
          completeRemove(affectedRentalIds)

          showToast({
            content: (
              <TagToastContent
                message={intl.formatMessage(
                  { id: 'Tags_removed' },
                  { count: affectedRentalIds.length, name: tag.name },
                )}
                onUndo={() => {
                  hideToast()
                  onMutationStart?.('add')
                  executeAddTags({
                    onCompleted: () => {
                      completeAdd(affectedRentalIds)
                    },
                    onError: (error, errorOptions) => {
                      onMutationError?.('add')
                      handleMutationError(
                        'addTagsForRentals',
                        'Tags_add_error',
                      )(error, errorOptions)
                    },
                    variables: {
                      input: {
                        orgID: orgId,
                        rentalIDs: affectedRentalIds,
                        tags: [
                          { color: tag.color as TAG_COLOR, name: tag.name },
                        ],
                      },
                    },
                  })
                }}
              />
            ),
            variant: 'success',
          })
        },
        onError: (error, errorOptions) => {
          onMutationError?.('remove')
          handleMutationError('removeTagsForRentals', 'Tags_remove_error')(
            error,
            errorOptions,
          )
        },
        variables: {
          input: {
            orgID: orgId,
            rentalIDs: affectedRentalIds,
            tagIDs: [tag.id],
          },
        },
      })
    },
    [
      orgId,
      getRentalIdsWithTag,
      onMutationStart,
      executeRemoveTags,
      completeRemove,
      showToast,
      intl,
      hideToast,
      executeAddTags,
      completeAdd,
      onMutationError,
      handleMutationError,
      onNoAffected,
    ],
  )

  return {
    addTag,
    isAddingTag,
    isRemovingTag,
    removeTag,
  }
}
